import React, { useEffect } from "react";
import "./App.scss";
import { createClient } from "@supabase/supabase-js";
import { createBrowserRouter, RouterProvider } from "react-router-dom";
import { 
  ThemeProvider,
  createTheme,
  useColorScheme
} from "@mui/material/styles";
import { useTranslation } from 'react-i18next';
import CssBaseline from "@mui/material/CssBaseline";
import Layout from "./layout/index";
import Box from "@mui/material/Box";
import Card from "@mui/material/Card";
import CardContent from "@mui/material/CardContent";
import AppContext from "./utils/context";
import DashboardView from "./pages/dashboard";
import ProjectView from "./pages/projects";
import AccountView from "./pages/me";
import EventEmitter from "./utils/eventEmitter";
import { useDialogs } from "@toolpad/core/useDialogs";
import { Auth } from "@supabase/auth-ui-react";
import { ThemeSupa } from "@supabase/auth-ui-shared";
import LogView from "./pages/logs";
import NotificationView from "./pages/notifications";
import TableView from "./pages/tables";

const darkTheme = createTheme({
  colorSchemes: {
    dark: true,
  },
});


const supabase = createClient(
  window.api.base_url,
  window.api.public_key,
  window.api.config
);

function App() {
  const { mode, setMode } = useColorScheme();
  const { t, i18n } = useTranslation();
  const [session, setSession] = React.useState(null);
  const [events, setEvents] = React.useState(null);
  const [userInfo, setUserInfo] = React.useState(null);

  const router = createBrowserRouter([
    {
      path: "/",
      element: <Layout />,
      children: [
        {
          path: "/",
          element: <DashboardView />,
        },
        {
          path: "/project/:projectId",
          element: <ProjectView />,
        },
        {
          path: "/project/:projectId/instance/:instanceId",
          element: <ProjectView />,
        },
        {
          path: "/logs",
          element: <LogView />,
        },
        {
          path: "/notifications",
          element: <NotificationView />,
        },
        {
          path: "/t/:pageSlug",
          element: <TableView />,
        },
      ]
    },
  ]);

  useEffect(() => {
    setEvents(new EventEmitter());
    setMode("system");
    supabase.auth.getSession().then(({ data: { session } }) => {
      setSession(session);
    });

    const {
      data: { subscription },
    } = supabase.auth.onAuthStateChange((_event, session) => {
      setSession(session);
    });

    return () => subscription.unsubscribe();
  }, []);

  const fetchUserInfo = async () => {
    const { data, error } = await supabase.rpc("get_user_info");
    if (error) {
      events.dispatch("alert-message", {
        data: { message: t('commons.errors.fetching'), severity: "error" },
      });
    } else {
      setUserInfo(data);
    }
  };
  
  React.useEffect(() => {
    if(!events || !session) return;
    const off = events.on("refresh-user", () => fetchUserInfo());
    fetchUserInfo();
    const intervalId = setInterval(fetchUserInfo, 300000);
    return () => {
      off();
      clearInterval(intervalId);
    };
  }, [events, session]);

  const AuthView = (
    <Box
      display="flex"
      justifyContent="center"
      alignItems="center"
      minHeight="100vh"
    >
      <Card sx={{ minWidth: 275 }}>
        <CardContent>
          <Auth
            supabaseClient={supabase}
            appearance={{ theme: ThemeSupa, style:{
              input: {color:'inherit'}
            } }}
            providers={['azure']}
            options={{ scopes: 'email'}}
            view="sign_in"
          />
        </CardContent>
      </Card>
    </Box>
  );

  const AppView = (
    <>
      {supabase && router && (
        <AppContext.Provider
          value={{
            database: supabase,
            router: router,
            events: events,
            user: userInfo
          }}
        >
            <RouterProvider router={router} />
        </AppContext.Provider>
      )}
    </>
  );

  
  return (    
    <ThemeProvider theme={darkTheme}>
      <CssBaseline enableColorScheme />
      {(session ? AppView : AuthView)}
    </ThemeProvider>    
  );


}

export default App;
